import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Wallet } from "lucide-react";
import { useOperator } from "@/components/mobile/operators";

const Ctx = createContext({ acc: null, own: false, choice: "" });
export const useUnoAccount = () => useContext(Ctx);

export const UnoAccountProvider = ({ children }) => {
  const op = useOperator();
  const [acc, setAcc] = useState(null);
  const [pick, setPickState] = useState(() => localStorage.getItem("uno-account-pick") || "own");
  const setPick = (k) => { setPickState(k); localStorage.setItem("uno-account-pick", k); };
  const reload = useCallback(() => {
    if (op.key !== "unomobile") { setAcc(null); return; }
    api.get("/unomobile/account").then(({ data }) => setAcc(data)).catch(() => setAcc(null));
  }, [op.key]);
  useEffect(() => { reload(); }, [reload]);
  const live = !!acc?.portal_on && !!acc?.ready;
  const own = live && (pick === "own" || !acc.allow_markoo);
  const value = { acc, own, choice: live ? (own ? "own" : "markoo") : "", setPick, setAcc, reload };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

const CredForm = ({ acc, onDone }) => {
  const { setAcc } = useUnoAccount();
  const [f, setF] = useState({ username: acc?.username || "", password: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.username.trim().length < 2 || !f.password) { toast.error("Inserisci utente e password del tuo account Uno Mobile"); return; }
    setBusy(true);
    try { const { data } = await api.put("/unomobile/account", f); setAcc(data); toast.success("Account Uno Mobile collegato: d'ora in poi le pratiche partono dal tuo account"); onDone?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Credenziali non salvate"); }
    finally { setBusy(false); }
  };
  return <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid="uno-acc-form">
    <div><Label className="text-xs">Utente Uno Mobile</Label><Input className="mt-1" autoComplete="off" value={f.username} onChange={(e) => setF((s) => ({ ...s, username: e.target.value }))} data-testid="uno-acc-username" /></div>
    <div><Label className="text-xs">Password</Label><Input className="mt-1" type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF((s) => ({ ...s, password: e.target.value }))} data-testid="uno-acc-password" /></div>
    <Button onClick={save} disabled={busy} data-testid="uno-acc-save">{busy ? "Verifico…" : "Verifica e salva"}</Button>
  </div>;
};

const PayPick = () => {
  const { own, setPick } = useUnoAccount();
  const opt = (k, title, hint) => <button key={k} type="button" onClick={() => setPick(k)} className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${(k === "own") === own ? "border-primary bg-primary/10" : "border-border hover:border-primary/60"}`} data-testid={`uno-acc-pay-${k}`}>
    <span className="font-medium">{title}</span><span className="block text-xs text-muted-foreground">{hint}</span></button>;
  return <div className="grid sm:grid-cols-2 gap-2 max-w-2xl" data-testid="uno-acc-pay">
    {opt("own", "Il mio account Uno Mobile", "paghi con il tuo e-wallet Uno Mobile, nessun addebito sul Plafond")}
    {opt("markoo", "Credito Markoo", "usiamo l'account Markoo e scaliamo dal tuo Plafond")}
  </div>;
};

export const UnoAccountBar = () => {
  const { acc, setAcc } = useUnoAccount();
  const [edit, setEdit] = useState(false);
  if (!acc?.enabled) return null;
  const remove = async () => {
    try { const { data } = await api.delete("/unomobile/account"); setAcc(data); toast.success("Credenziali rimosse: torni a usare l'account Markoo"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita"); }
  };
  const bad = acc.status === "errore";
  return <Card className="p-4 space-y-3 glow-border" data-testid="uno-acc-card">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-medium flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> Il tuo account Uno Mobile</p>
        <p className={`text-xs mt-0.5 ${bad ? "text-rose-500" : "text-muted-foreground"}`} data-testid="uno-acc-status">{!acc.ready ? "L'amministrazione ti permette di usare le credenziali che ti ha dato Uno Mobile. Finché non le inserisci usiamo l'account Markoo e il tuo Plafond." : bad ? "Uno Mobile ha rifiutato utente o password: le pratiche sono bloccate finché non li aggiorni." : `Collegato come ${acc.username}${acc.ewallet != null ? ` · e-wallet ${euro(acc.ewallet)}` : ""}. Le pratiche partono dal tuo account e si pagano con il tuo e-wallet Uno Mobile.`}</p>
        {!acc.portal_on && acc.ready && <p className="text-xs text-amber-600 mt-0.5" data-testid="uno-acc-portal-off">Il collegamento automatico con Uno Mobile è spento: per ora le pratiche le lavora l'amministrazione.</p>}
      </div>
      {acc.ready && <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setEdit((v) => !v)} data-testid="uno-acc-edit">{edit ? "Chiudi" : "Cambia credenziali"}</Button><Button size="sm" variant="ghost" onClick={remove} data-testid="uno-acc-remove">Rimuovi</Button></div>}
    </div>
    {(!acc.ready || edit || bad) && <CredForm acc={acc} onDone={() => setEdit(false)} />}
    {acc.ready && acc.allow_markoo && acc.portal_on && <div className="space-y-1.5"><p className="text-xs font-medium flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Con quale credito lavori?</p><PayPick /></div>}
  </Card>;
};
